import { useQuery } from 'data-fetching-client';
import { useMemo } from 'react';
import { GET_GROWTH_PURCHASE_OPTIONS_QUERY } from '../../../__generated__/chirp/com/hubspot/buying/rpc/ProductRpc.dfc.new';
import { GET_GROWTH_PURCHASE_OPTIONS_QUERY as GET_GROWTH_PURCHASE_OPTIONS_QUERY_NO_AUTH } from '../../../__generated__/chirp/com/hubspot/buying/rpc/PublicProductRpc.dfc.new';
import { useDisplayedCurrencyCode } from './useCurrencyDetails';
import { useIsInAppAuth } from './useUserInfo';
const growthPopQueryToUse = isAppAuth => isAppAuth ? GET_GROWTH_PURCHASE_OPTIONS_QUERY : GET_GROWTH_PURCHASE_OPTIONS_QUERY_NO_AUTH;

/**
 * Base hook for querying growth pop purchase options. This should not be used directly,
 * instead use one of the more specific hooks below.
 * @param productApiNames - The product API names to query
 * @returns The products, currency code, error, and loading state
 */
const useGrowthPopPurchaseOptions = ({
  productsToPurchase
}) => {
  const isAppAuth = useIsInAppAuth();
  const currencyCode = useDisplayedCurrencyCode();
  const {
    data,
    error,
    loading
  } = useQuery(growthPopQueryToUse(isAppAuth), {
    variables: {
      products: productsToPurchase,
      currencyCode
    },
    skip: !currencyCode
  });
  return {
    products: (data === null || data === void 0 ? void 0 : data.result.products) || {},
    currencyCode: (data === null || data === void 0 ? void 0 : data.result.currencyCode) || null,
    error,
    loading
  };
};

/**
 * Returns the best price for a list of products.
 *
 * @param productsToPurchase - Array of products to purchase (productApiName, quantity)
 * @returns The best prices for each product, currency code, error, and loading state
 */
export const useBestPriceForProducts = ({
  productsToPurchase
}) => {
  const {
    products,
    error,
    loading,
    currencyCode
  } = useGrowthPopPurchaseOptions({
    productsToPurchase
  });
  const bestPriceMap = useMemo(() => {
    const pricesMap = {};
    for (const productApiName of Object.keys(products)) {
      if (products[productApiName].bestPrice) {
        var _products$productApiN, _products$productApiN2, _products$productApiN3, _products$productApiN4;
        const monthlyPrice = (_products$productApiN = products[productApiName].bestPrice) !== null && _products$productApiN !== void 0 && _products$productApiN.monthlyPrice ? Number((_products$productApiN2 = products[productApiName].bestPrice) === null || _products$productApiN2 === void 0 ? void 0 : _products$productApiN2.monthlyPrice) : undefined;
        const priceForCommittedPeriod = (_products$productApiN3 = products[productApiName].bestPrice) !== null && _products$productApiN3 !== void 0 && _products$productApiN3.priceForCommittedPeriod ? Number((_products$productApiN4 = products[productApiName].bestPrice) === null || _products$productApiN4 === void 0 ? void 0 : _products$productApiN4.priceForCommittedPeriod) : undefined;
        pricesMap[productApiName] = {
          monthlyPrice,
          priceForCommittedPeriod
        };
      }
    }
    return pricesMap;
  }, [products]);
  return {
    bestPriceMap,
    error,
    loading,
    currencyCode
  };
};

/**
 * Returns all per month prices for a product.
 *
 * @param productApiName - The product API name to query
 * @param quantity - The quantity of the product to purchase
 * @returns The annual and monthly prices, currency code, error, and loading state
 */
export const usePerMonthPricesForRecurringTerms = ({
  productApiName,
  quantity = 1
}) => {
  var _products$productApiN5, _products$productApiN6, _products$productApiN7, _products$productApiN8;
  const {
    products,
    currencyCode,
    error,
    loading
  } = useGrowthPopPurchaseOptions({
    productsToPurchase: [{
      productApiName,
      quantity
    }]
  });
  const annualPrice = (_products$productApiN5 = products[productApiName]) !== null && _products$productApiN5 !== void 0 && (_products$productApiN5 = _products$productApiN5.purchaseOptions) !== null && _products$productApiN5 !== void 0 && (_products$productApiN5 = _products$productApiN5.ANNUAL) !== null && _products$productApiN5 !== void 0 && _products$productApiN5.monthlyPrice ? Number((_products$productApiN6 = products[productApiName]) === null || _products$productApiN6 === void 0 || (_products$productApiN6 = _products$productApiN6.purchaseOptions) === null || _products$productApiN6 === void 0 || (_products$productApiN6 = _products$productApiN6.ANNUAL) === null || _products$productApiN6 === void 0 ? void 0 : _products$productApiN6.monthlyPrice) : undefined;
  const monthlyPrice = (_products$productApiN7 = products[productApiName]) !== null && _products$productApiN7 !== void 0 && (_products$productApiN7 = _products$productApiN7.purchaseOptions) !== null && _products$productApiN7 !== void 0 && (_products$productApiN7 = _products$productApiN7.MONTHLY) !== null && _products$productApiN7 !== void 0 && _products$productApiN7.monthlyPrice ? Number((_products$productApiN8 = products[productApiName]) === null || _products$productApiN8 === void 0 || (_products$productApiN8 = _products$productApiN8.purchaseOptions) === null || _products$productApiN8 === void 0 || (_products$productApiN8 = _products$productApiN8.MONTHLY) === null || _products$productApiN8 === void 0 ? void 0 : _products$productApiN8.monthlyPrice) : undefined;
  return {
    annualPrice,
    monthlyPrice,
    currencyCode,
    error,
    loading
  };
};

/**
 * Returns the per month price for a product for a given term.
 *
 * @param productApiName - The product API name to query
 * @param quantity - The quantity of the product to purchase
 * @param term - The term to query
 * @returns The per month price, currency code, error, and loading state
 */
export const usePerMonthPriceForTerm = ({
  productApiName,
  quantity = 1,
  term = 'ANNUAL'
}) => {
  const {
    annualPrice,
    monthlyPrice,
    currencyCode,
    error,
    loading
  } = usePerMonthPricesForRecurringTerms({
    productApiName,
    quantity
  });
  const price = term === 'ANNUAL' ? annualPrice : monthlyPrice;
  return {
    price,
    currencyCode,
    error,
    loading
  };
};

/**
 * Returns the promotion IDs for a product for a given term.
 *
 * @param productApiName - The product API name to query
 * @param quantity - The quantity of the product to purchase
 * @param term - The term to query
 * @returns The promotion IDs, error, and loading state
 */
const useProductPromotionIdsForTerm = ({
  productApiName,
  term = 'ANNUAL',
  quantity = 1
}) => {
  const {
    products,
    error,
    loading
  } = useGrowthPopPurchaseOptions({
    productsToPurchase: [{
      productApiName,
      quantity
    }]
  });
  const promotionIds = useMemo(() => {
    var _product$purchaseOpti, _purchaseOption$disco;
    const product = products[productApiName];
    if (!product) {
      return [];
    }
    const purchaseOption = (_product$purchaseOpti = product.purchaseOptions) === null || _product$purchaseOpti === void 0 ? void 0 : _product$purchaseOpti[term];
    if (!purchaseOption) {
      return [];
    }
    return ((_purchaseOption$disco = purchaseOption.discounts) === null || _purchaseOption$disco === void 0 ? void 0 : _purchaseOption$disco.map(discount => discount.promotionId)) || [];
  }, [products, term, productApiName]);
  return {
    promotionIds,
    error,
    loading
  };
};

/**
 * Returns the promotion IDs for a product for all terms.
 *
 * @param productApiName - The product API name to query
 * @param quantity - The quantity of the product to purchase
 * @returns The promotion IDs, error, and loading state
 */
export const useProductPromotionIds = ({
  productApiName,
  quantity = 1
}) => {
  var _ref;
  const {
    promotionIds: annualPromotionIds,
    error: annualError,
    loading: annualLoading
  } = useProductPromotionIdsForTerm({
    productApiName,
    term: 'ANNUAL',
    quantity
  });
  const {
    promotionIds: monthlyPromotionIds,
    error: monthlyError,
    loading: monthlyLoading
  } = useProductPromotionIdsForTerm({
    productApiName,
    term: 'MONTHLY',
    quantity
  });
  const {
    promotionIds: oneTimeFeePromotionIds,
    error: oneTimeFeeError,
    loading: oneTimeFeeLoading
  } = useProductPromotionIdsForTerm({
    productApiName,
    term: 'ONE_TIME_FEE',
    quantity
  });
  return {
    promotionIds: [...annualPromotionIds, ...monthlyPromotionIds, ...oneTimeFeePromotionIds],
    error: (_ref = annualError !== null && annualError !== void 0 ? annualError : monthlyError) !== null && _ref !== void 0 ? _ref : oneTimeFeeError,
    loading: annualLoading || monthlyLoading || oneTimeFeeLoading
  };
};